import React, { useState, useRef, useEffect } from 'react';
import { generateCreativeText } from '../services/geminiService';
import { ChatMessage, LoadingState } from '../types';

export const ScriptStudio: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'init',
      role: 'model',
      content: 'CORE LOADED. I AM THE DIRECTOR. GIVE ME A PREMISE, A LOGLINE, OR A FRAGMENT OF CHAOS. LET\'S BUILD A SCRIPT.',
      timestamp: Date.now()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState<LoadingState>(LoadingState.IDLE);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading === LoadingState.LOADING) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(LoadingState.LOADING);

    try {
      const historyContext = messages.map(m => `${m.role === 'user' ? 'CLIENT' : 'DIRECTOR'}: ${m.content}`).join('\n');
      const fullPrompt = `${historyContext}\nCLIENT: ${userMsg.content}\nDIRECTOR:`;
      
      const responseText = await generateCreativeText(fullPrompt);
      
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        content: responseText,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);
      setLoading(LoadingState.SUCCESS);
    } catch (error) {
      setLoading(LoadingState.ERROR);
      setMessages(prev => [...prev, {
        id: 'error',
        role: 'model',
        content: 'FATAL_ERROR: CONNECTION_LOST. REBOOT_SYSTEM.',
        timestamp: Date.now()
      }]);
    }
  };

  return (
    <div className="pt-24 pb-12 px-4 h-screen flex flex-col max-w-6xl mx-auto scanlines">
      <div className="mb-6 flex justify-between items-end border-b-2 border-zinc-800 pb-4">
        <div>
          <h2 className="font-display text-4xl text-white tracking-tighter uppercase font-black">SCRIPT.EXE</h2>
          <p className="font-mono text-acid text-xs opacity-70">NEURAL_NARRATIVE_ENGINE_V3.1</p>
        </div>
        <div className="hidden md:block font-mono text-[10px] text-zinc-600">
          U_IP: {Math.floor(Math.random() * 255)}.{Math.floor(Math.random() * 255)}.0.1
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar bg-black/40 border-2 border-zinc-800 p-6 rounded-none mb-6 shadow-[inset_0_0_30px_rgba(0,0,0,1)]">
        {messages.map((msg) => (
          <div key={msg.id} className={`mb-8 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] md:max-w-[70%] p-5 font-mono text-sm leading-relaxed ${
              msg.role === 'user' 
                ? 'bg-zinc-900 text-white border-r-4 border-acid' 
                : 'bg-void text-zinc-300 border-l-4 border-glitch relative'
            }`}>
              <div className={`text-[9px] mb-3 uppercase tracking-[0.2em] font-bold ${msg.role === 'user' ? 'text-zinc-500 text-right' : 'text-glitch'}`}>
                {msg.role === 'user' ? '/// CLIENT_INPUT' : '/// DIRECTOR_OUTPUT'}
              </div>
              <div className="whitespace-pre-wrap">{msg.content}</div>
              {msg.role === 'model' && (
                <div className="absolute top-0 right-0 p-1 opacity-10">
                  <div className="w-2 h-2 bg-glitch rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading === LoadingState.LOADING && (
          <div className="flex items-center gap-3 text-acid font-mono text-xs pl-2">
            <span className="w-2 h-2 bg-acid rounded-full animate-ping"></span>
            PROCESSING_NARRATIVE_STREAMS...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-4 shrink-0 bg-zinc-900 p-2 border-2 border-zinc-800 shadow-[8px_8px_0px_0px_#000]">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="INITIALIZE_SEQUENCE..."
          className="flex-1 bg-transparent text-white font-mono p-4 focus:outline-none placeholder-zinc-700 text-base"
        />
        <button
          onClick={handleSend}
          disabled={loading === LoadingState.LOADING}
          className="btn-8bit !py-4 !px-10 text-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          EXECUTE
        </button>
      </div>
    </div>
  );
};